<template>
    <section
        id="partners"
        ref="partnersSection"
        class="partners-section"
        :class="{ 'is-visible': sectionVisible }"
        aria-label="Clientes"
    >
        <div class="partners-content">
            <div class="partners-intro">
                <span class="section-tag">Clientes</span>
                <h2 class="section-title-modern">
                    Soluções em operação para rotinas críticas e alto volume.
                </h2>
                <p class="section-description">
                    Desenvolvemos plataformas, integrações e automações para empresas que precisam de estabilidade,
                    rastreabilidade e evolução contínua em ambientes reais de uso.
                </p>
            </div>

            <div class="partners-grid" aria-label="Clientes atendidos">
                <div class="partners-grid-copy">
                    <span>Clientes atendidos</span>
                    <p>Algumas operações que confiam na Mazzitech para transformar processo em software.</p>
                </div>
                <component
                    :is="client.url ? 'a' : 'div'"
                    v-for="(client, index) in allClients"
                    :key="client.name"
                    class="partner-card"
                    :class="{
                        'partner-card-muted': client.muted,
                        'partner-card-dark-logo': client.darkLogo,
                        'partner-card-more': client.more,
                    }"
                    :style="{ transitionDelay: `${70 * index}ms` }"
                    :href="client.url"
                    :target="client.url ? '_blank' : null"
                    :rel="client.url ? 'noopener noreferrer' : null"
                    :aria-label="client.url ? `Abrir site do cliente ${client.name}` : client.name"
                >
                    <img
                        v-if="!client.more"
                        class="partner-logo"
                        :width="client.width || '170px'"
                        :src="client.image"
                        :alt="`Logo do cliente ${client.name}`"
                        loading="lazy"
                    />
                    <strong v-else>+15</strong>
                    <span class="partner-name">{{ client.more ? "clientes atendidos" : client.name }}</span>
                </component>
            </div>

            <div ref="impactArea" class="impact-area" :class="{ 'is-visible': impactVisible }">
                <div class="impact-head">
                    <span class="section-tag">Impacto</span>
                    <h3>Indicadores de soluções em produção.</h3>
                    <p>
                        Números que representam projetos sob medida operando em ambientes com volume, continuidade
                        e responsabilidade técnica.
                    </p>
                </div>

                <div class="impact-block" aria-label="Indicadores de impacto">
                    <article
                        v-for="(metric, index) in metrics"
                        :key="metric.label"
                        class="impact-card"
                        :style="{ transitionDelay: `${90 * index}ms` }"
                    >
                        <strong>{{ metric.displayValue }}</strong>
                        <span>{{ metric.label }}</span>
                        <p>{{ metric.description }}</p>
                    </article>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import logoBJ from "@/assets/images/logo-b&j.png";
import logoPbb from "@/assets/images/logo-pbb.png";
import logo123 from "@/assets/images/logo-123.png";
import logoAccc from "@/assets/images/logo-accc.png";
import logoInocencio from "@/assets/images/logo-inocencio.png";
import logoCarvalho from "@/assets/images/logo-carvalho.png";
import logoLavepay from "@/assets/images/logo-lavepay.png";
import logoOnix from "@/assets/images/logo-onix.png";
import logoAfx from "@/assets/images/logo-afx.png";
import logoAxess from "@/assets/images/logo-axess.png";
import logoRafa from "@/assets/images/logo-rafa.webp";

export default {
    data() {
        return {
            sectionVisible: false,
            impactVisible: false,
            hasAnimatedMetrics: false,
            observer: null,
            metricAnimation: null,
            metrics: [
                {
                    value: "+2,5 milhões",
                    displayValue: "+2,5 milhões",
                    target: 2500000,
                    type: "million",
                    label: "usuários impactados",
                    description: "Soluções utilizadas em operações com grande volume de acessos, dados e rotinas.",
                },
                {
                    value: "+30",
                    displayValue: "+30",
                    target: 30,
                    type: "number",
                    label: "projetos entregues",
                    description: "Sistemas, automações e integrações criados sob medida para diferentes segmentos.",
                },
                {
                    value: "24/7",
                    displayValue: "24/7",
                    target: null,
                    type: "static",
                    label: "operações críticas",
                    description: "Projetos pensados para continuidade, rastreabilidade e sustentação em produção.",
                },
            ],
            clients: [
                {
                    name: "Brizola e Japur",
                    image: logoBJ,
                    url: "https://brizolaejapur.com.br",
                },
                {
                    name: "Paoli Balbino e Barros",
                    image: logoPbb,
                    url: "https://pbbadvogados.com.br",
                },
                {
                    name: "Acerbi Campagnaro Colnago Cabral",
                    image: logoAccc,
                    width: "220px",
                    url: "https://acerbicampagnaro.com.br",
                },
                {
                    name: "Inocêncio de Paula",
                    image: logoInocencio,
                    width: "220px",
                    url: "https://inocenciodepaulaadvogados.com.br",
                },
                {
                    name: "123 Milhas",
                    image: logo123,
                    url: "https://rj123milhas.com.br",
                },
            ],
            additionalClients: [
                {
                    name: "Grupo Carvalho",
                    image: logoCarvalho,
                },
                {
                    name: "Lavepay",
                    image: logoLavepay,
                    width: "120px",
                },
                {
                    name: "Onix Autolocadora",
                    image: logoOnix,
                },
                {
                    name: "AFX",
                    image: logoAfx,
                },
                {
                    name: "Axess",
                    image: logoAxess,
                },
                {
                    name: "Rafa Repasses",
                    image: logoRafa,
                    width: "150px",
                    darkLogo: true,
                },
            ],
        };
    },
    computed: {
        allClients() {
            return [
                ...this.clients,
                ...this.additionalClients.map((client) => ({
                    ...client,
                    muted: true,
                })),
                {
                    name: "Mais de 15 clientes atendidos",
                    more: true,
                },
            ];
        },
    },
    mounted() {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            this.sectionVisible = true;
            this.impactVisible = true;
            this.finishMetrics();
            return;
        }

        this.observer = new IntersectionObserver(this.handleIntersection, {
            threshold: 0.24,
            rootMargin: "0px 0px -12% 0px",
        });

        if (this.$refs.partnersSection) {
            this.observer.observe(this.$refs.partnersSection);
        }

        if (this.$refs.impactArea) {
            this.observer.observe(this.$refs.impactArea);
        }
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }

        if (this.metricAnimation) {
            cancelAnimationFrame(this.metricAnimation);
        }
    },
    methods: {
        handleIntersection(entries) {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                if (entry.target === this.$refs.partnersSection) {
                    this.sectionVisible = true;
                }

                if (entry.target === this.$refs.impactArea) {
                    this.impactVisible = true;
                    this.startMetricsAnimation();
                }
            });
        },
        startMetricsAnimation() {
            if (this.hasAnimatedMetrics) {
                return;
            }

            this.hasAnimatedMetrics = true;
            this.metrics.forEach((metric) => {
                if (metric.type !== "static") {
                    metric.displayValue = "+0";
                }
            });

            const duration = 1450;
            const start = performance.now();

            const step = (now) => {
                const progress = Math.min((now - start) / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 3);

                this.metrics.forEach((metric) => {
                    if (metric.type === "million") {
                        const current = metric.target * eased / 1000000;
                        metric.displayValue = progress === 1 ? metric.value : `+${current.toLocaleString("pt-BR", {
                            minimumFractionDigits: current < 1 ? 1 : 0,
                            maximumFractionDigits: 1,
                        })} milhões`;
                    }

                    if (metric.type === "number") {
                        metric.displayValue = `+${Math.round(metric.target * eased)}`;
                    }
                });

                if (progress < 1) {
                    this.metricAnimation = requestAnimationFrame(step);
                } else {
                    this.finishMetrics();
                }
            };

            this.metricAnimation = requestAnimationFrame(step);
        },
        finishMetrics() {
            this.metrics.forEach((metric) => {
                metric.displayValue = metric.value;
            });
        },
    },
};
</script>

<style lang="scss">
.partners-section {
    position: relative;
    background: #f7f7f5;
    padding: clamp(116px, 9vw, 156px) 0 clamp(128px, 9vw, 172px);
    border-bottom: 1px solid #ddddda;
    overflow: hidden;
    z-index: 0;
}

.partners-content {
    width: min(76vw, 1520px);
    margin: 0 auto;
}

.partners-intro {
    display: grid;
    grid-template-columns: 180px minmax(460px, 1.08fr) minmax(280px, 0.72fr);
    column-gap: clamp(36px, 4.5vw, 72px);
    align-items: end;
    margin-bottom: 58px;
    opacity: 0;
    transform: translateY(26px);
    transition: opacity 0.82s ease, transform 0.82s cubic-bezier(0.22, 1, 0.36, 1);
}

.partners-section.is-visible .partners-intro {
    opacity: 1;
    transform: translateY(0);
}

.section-tag {
    display: inline-block;
    color: #5d5d57;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    margin-bottom: 16px;
}

.section-title-modern {
    font-family: "Space Grotesk", sans-serif !important;
    color: #111111;
    font-size: clamp(2rem, 3.8vw, 3.35rem);
    font-weight: 700;
    line-height: 1.08;
    margin-bottom: 18px;
}

.section-description {
    color: #565650;
    font-size: 17px;
    line-height: 1.7;
    max-width: 620px;
    margin: 0 auto;
}

.partners-intro .section-tag {
    margin-bottom: 14px;
}

.partners-intro .section-title-modern {
    grid-column: 2;
    text-align: left;
    margin-left: 0;
}

.partners-intro .section-description {
    grid-column: 3;
    max-width: 430px;
    margin: 0;
    text-align: left;
}

.partners-grid {
    display: grid;
    grid-template-columns: minmax(220px, 0.72fr) repeat(6, minmax(110px, 1fr));
    gap: 0;
    align-items: stretch;
    border-top: 1px solid #d8d8d3;
    border-bottom: 1px solid #d8d8d3;
    opacity: 0;
    transform: translateY(26px);
    transition: opacity 0.84s ease 0.12s, transform 0.84s cubic-bezier(0.22, 1, 0.36, 1) 0.12s;
}

.partners-section.is-visible .partners-grid {
    opacity: 1;
    transform: translateY(0);
}

.partners-grid-copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 150px;
    padding: 26px 28px 26px 0;
    grid-row: span 2;
    opacity: 0;
    transform: translateX(-16px);
    transition: opacity 0.72s ease 0.25s, transform 0.72s cubic-bezier(0.22, 1, 0.36, 1) 0.25s;
}

.partners-section.is-visible .partners-grid-copy {
    opacity: 1;
    transform: translateX(0);
}

.partners-grid-copy span {
    display: block;
    color: #0f4efb;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-bottom: 14px;
}

.partners-grid-copy p {
    color: #555550;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
}

.partner-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    min-height: 150px;
    padding: 24px 18px;
    background: transparent;
    border-left: 1px solid #d8d8d3;
    text-decoration: none;
    opacity: 0;
    transform: translateY(18px);
    transition: opacity 0.58s ease, transform 0.58s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease;
}

.partner-card-muted .partner-logo {
    max-height: 48px;
    opacity: 0.58;
}

.partner-card-dark-logo .partner-logo {
    max-height: 48px;
    padding: 0;
    background: transparent;
    border-radius: 0;
    filter: grayscale(1) brightness(0.58) contrast(1.25);
    opacity: 0.72;
}

.partner-card-dark-logo:hover .partner-logo {
    opacity: 1;
    filter: grayscale(1) brightness(0.48) contrast(1.35);
}

.partner-card:hover {
    background: rgba(255, 255, 255, 0.42);
    transform: translateY(-4px);
}

.partner-card:hover .partner-logo {
    opacity: 1;
    filter: grayscale(0);
    transform: scale(1.035);
}

.partners-section.is-visible .partner-card {
    opacity: 1;
    transform: translateY(0);
}

.partners-section.is-visible .partner-card-muted:hover {
    transform: translateY(-3px);
}

.partners-section.is-visible .partner-card:hover {
    transform: translateY(-4px);
}

.partner-card-more {
    cursor: default;
}

.partner-card-more strong {
    color: #111111;
    font-family: "Space Grotesk", sans-serif !important;
    font-size: clamp(2.15rem, 2.8vw, 3.45rem);
    font-weight: 700;
    letter-spacing: 0;
    line-height: 0.95;
}

.partner-card-more:hover {
    transform: translateY(0);
}

.partner-logo {
    max-height: 58px;
    max-width: 100%;
    object-fit: contain;
    filter: grayscale(1);
    opacity: 0.72;
    transition: filter 0.28s ease, opacity 0.28s ease, transform 0.28s ease;
}

.partner-name {
    min-height: 28px;
    color: #676762;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    line-height: 1.25;
    text-align: center;
    text-transform: uppercase;
}

.impact-area {
    margin-top: clamp(96px, 7vw, 132px);
    padding-top: clamp(66px, 5vw, 88px);
    border-top: 1px solid #d8d8d3;
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.78s ease, transform 0.78s ease;
}

.impact-area.is-visible {
    opacity: 1;
    transform: translateY(0);
}

.impact-head {
    display: grid;
    grid-template-columns: 180px minmax(460px, 1fr) minmax(280px, 0.72fr);
    column-gap: clamp(36px, 4.5vw, 72px);
    align-items: end;
    margin-bottom: 34px;
}

.impact-head .section-tag {
    margin-bottom: 9px;
}

.impact-head h3 {
    grid-column: 2;
    color: #111111;
    font-family: "Space Grotesk", sans-serif !important;
    font-size: clamp(2.1rem, 3.3vw, 4.2rem);
    font-weight: 700;
    letter-spacing: 0;
    line-height: 0.98;
    margin: 0;
}

.impact-head p {
    grid-column: 3;
    max-width: 430px;
    color: #555550;
    font-size: 17px;
    line-height: 1.7;
    margin: 0;
}

.impact-block {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 0;
    background: #111111;
    border: 1px solid #111111;
    overflow: hidden;
}

.impact-card {
    position: relative;
    min-height: 250px;
    padding: clamp(28px, 2.35vw, 42px);
    color: #ffffff;
    border-right: 1px solid rgba(255, 255, 255, 0.16);
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.68s ease, transform 0.68s ease;
}

.impact-area.is-visible .impact-card {
    opacity: 1;
    transform: translateY(0);
}

.impact-card:last-child {
    border-right: 0;
}

.impact-card strong {
    display: block;
    color: #ffffff;
    font-family: "Space Grotesk", sans-serif !important;
    font-size: clamp(2.5rem, 2.9vw, 4.65rem);
    font-weight: 700;
    letter-spacing: 0;
    line-height: 0.92;
    margin-bottom: 18px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    max-width: 100%;
    transform-origin: left center;
}

.impact-card:first-child strong {
    font-size: clamp(2.35rem, 2.65vw, 4.15rem);
}

.impact-card span {
    display: block;
    color: #ffffff;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.12em;
    line-height: 1.35;
    margin-bottom: 18px;
    text-transform: uppercase;
}

.impact-card p {
    max-width: 360px;
    color: rgba(255, 255, 255, 0.68);
    font-size: 15px;
    line-height: 1.7;
    margin: 0;
}

@media (prefers-reduced-motion: reduce) {
    .partners-grid,
    .partners-grid-copy,
    .impact-area,
    .impact-card,
    .partner-card,
    .partners-intro {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
    }
}

@media (max-width: 1200px) {
    .partners-content {
        width: 84vw;
    }

    .partners-intro,
    .impact-head {
        grid-template-columns: 150px minmax(0, 1fr);
        align-items: start;
    }

    .partners-intro .section-description,
    .impact-head p {
        grid-column: 2;
        max-width: 700px;
        margin-top: -10px;
    }

    .partners-grid {
        grid-template-columns: repeat(6, minmax(110px, 1fr));
    }

    .partners-grid-copy {
        grid-column: 1 / -1;
        grid-row: auto;
        min-height: 0;
        padding: 0 0 22px;
        border-bottom: 1px solid #d8d8d3;
    }
}

@media (max-width: 960px) {
    .partners-section {
        padding-top: 320px;
    }

    .partners-content {
        width: 90%;
    }

    .partners-intro,
    .impact-head,
    .impact-block {
        grid-template-columns: 1fr;
    }

    .partners-intro .section-title-modern,
    .partners-intro .section-description,
    .impact-head h3,
    .impact-head p {
        grid-column: 1;
    }

    .partners-intro .section-description,
    .impact-head p {
        margin-top: 0;
    }

    .impact-area {
        margin-top: 72px;
        padding-top: 40px;
    }

    .impact-head {
        margin-bottom: 26px;
    }

    .impact-card {
        min-height: 0;
        border-right: 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.16);
    }

    .impact-card:last-child {
        border-bottom: 0;
    }

    .impact-card strong,
    .impact-card:first-child strong {
        font-size: clamp(2.65rem, 11vw, 4.2rem);
        white-space: normal;
    }
}

@media (max-width: 640px) {
    .partners-grid {
        grid-template-columns: 1fr 1fr;
    }

    .partner-card {
        min-height: 118px;
        padding: 18px 12px;
        border-left: 0;
        border-top: 1px solid #d8d8d3;
    }

    .partners-grid-copy {
        grid-column: 1 / -1;
    }
}
</style>
